<template>
  <div>
    <h3>Use Codes Reports</h3>
    <form class="form-inline">
      <div class="form-group">
        <select v-model="reportType" class="form-control">
          <option value=""/>
          <option value="active">Active codes</option>
          <option value="expired">Expired codes</option>
          <option value="all">Active and expired codes</option>
        </select>
      </div>
      <button class="btn btn-primary" @click.prevent="fetchReport">Fetch Report</button>
    </form>

    <hr>
    <p class="lead text-muted" v-if="loading"><i class="fa fa-circle-o-notch fa-spin"/> Loading...</p>
    <div v-if="useCodes" class="report-container">
      <div class="row">
        <div class="col-xs-4">
          <div class="panel panel-default">
            <div class="panel-body text-center">
              <h1>{{ useCodes.length }}</h1>
              <small class="text-muted">codes generated</small>
            </div>
          </div>
        </div>
        <div class="col-xs-4">
          <div class="panel panel-default">
            <div class="panel-body text-center">
              <h1>{{ numCodesUsed }}</h1>
              <small class="text-muted">codes used</small>
            </div>
          </div>
        </div>
        <div class="col-xs-4">
          <div class="panel panel-default">
            <div class="panel-body text-center">
              <h1>{{ percentageCodesUsed }}</h1>
              <small class="text-muted">usage</small>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-9">
          <h4>Magic Link Destination</h4>
          <div class="radio">
            <label for="toolkit">
              <input
                type="radio"
                value="toolkit"
                id="toolkit"
                v-model="magicLinkShortcut"> Toolkit Home
            </label>
          </div>

          <div class="radio">
            <label for="pathways">
              <input
                type="radio"
                value="pathways"
                id="pathways"
                v-model="magicLinkShortcut"> Pathways Home
            </label>
          </div>
          <div class="radio">
            <label for="other">
              <input
                type="radio"
                value=""
                id="other"
                v-model="magicLinkShortcut"> Other URL
            </label>
            <input
              v-if="magicLinkShortcut === ''"
              type="text"
              v-model="magicLinkDestination"
              class="form-control"
              value="https://goalbookapp.com/toolkit"
              placeholder="URL">
          </div>
        </div>
        <div class="col-sm-3">
          <ExportToCSVButton
            label="Export Magic Links"
            filename="magiclinks.csv"
            :headers="csvExportData.headers"
            :rows="csvExportData.rows"
            class="btn btn-block btn-primary" />
        </div>
      </div>
      <br>
      <p class="text-muted" v-if="loadingReportTable"><i class="fa fa-circle-o-notch fa-spin"/> Loading user table...</p>
      <v-client-table
        :columns="tableColumns"
        :data="tableRows"
        :options="tableOptions">
        <div slot="name" slot-scope="props">
          {{ props.row.name }}
          <br>
          <small class="text-muted">{{ props.row.user_uuid }}</small>
        </div>
        <div slot="date_generated" slot-scope="props">
          {{ formatTimestamp(props.row.date_generated, 'M/D/YY, h:mm a z') }}
          <br>
          <small class="text-muted">{{ props.row.uuid }}</small>
        </div>
        <div slot="expiration_date" slot-scope="props">
          {{ formatTimestamp(props.row.expiration_date, 'M/D/YY') }}
          <span class="label label-warning" v-if="isDatePassed(props.row.expiration_date)">Expired</span>
        </div>
        <span slot="used" slot-scope="props">{{ props.row.used && formatTimestamp(props.row.used, 'M/D/YY, h:mm a z') || '-' }}</span>
        <button
          slot="test"
          slot-scope="props"
          class="btn btn-default btn-xs"
          :disabled="!magicLinkShortcut && !magicLinkDestination"
          @click="testUseCode(props.row.uuid)">Test</button>
      </v-client-table>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import momentTz from 'moment-timezone'
import { ClientTable } from 'vue-tables-2'
import ExportToCSVButton from '@/scripts/ExportToCSVButton'
import Vue from 'vue'
Vue.use(ClientTable)

export default {
  name: 'UseCodesDomainReport',
  components: {
    ExportToCSVButton
  },
  props: {
    domainUuid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      reportType: 'active',
      loading: false,
      useCodes: null,
      magicLinkDestination: '',
      magicLinkShortcut: 'toolkit',
      tableColumns: [
        'name',
        'title',
        'toolkit_end_date',
        'pathways_end_date',
        'date_generated',
        'expiration_date',
        'used',
        'test'
      ],
      tableOptions: {
        headings: {
          name: 'Name',
          title: 'Title',
          toolkit_end_date: 'TK End Date',
          pathways_end_date: 'PW End Date',
          date_generated: 'Generated',
          expiration_date: 'Expires',
          used: 'Used',
          use_count: 'Use Count',
          test: ''
        },
        sortable: [
          'name',
          'title',
          'toolkit_end_date',
          'pathways_end_date',
          'date_generated',
          'expiration_date',
          'used'
        ],
        filterable: false,
        perPage: 50,
        perPageValues: [25, 50, 100],
        rowClassCallback(row) {
          return row.used && 'success'
        }
      }
    }
  },
  computed: {
    domainUsers() {
      return this.$angularWorld.domainInfo.usersUI.users
    },
    loadingDomainUsers() {
      return this.$angularWorld.domainInfo.usersUI.loading
    },
    loadingReportTable() {
      return this.loading || this.loadingDomainUsers
    },
    numCodesUsed() {
      return (this.useCodes || []).filter(uc => uc.use_count > 0).length
    },
    percentageCodesUsed() {
      return this.useCodes && this.useCodes.length > 0
        ? `${(this.numCodesUsed / this.useCodes.length * 100).toFixed(2)}%`
        : '-'
    },
    userMap() {
      let userMap = {}
      this.domainUsers.forEach(user => {
        userMap[user.uuid] = user
      })
      return userMap
    },
    tableRows() {
      return this.useCodes
        .map(uc => {
          let user = this.userMap[uc.user_uuid] || {}
          return Object.assign({}, uc, {
            name: user.name || '(not found)',
            title: user.title,
            email: user.email,
            toolkit_end_date: (user.toolkit_license && user.toolkit_license.end_date_fmt) || '-',
            pathways_end_date: (user.pathways_license && user.pathways_license.end_date_fmt) || '-'
          })
        })
        .sort((user1, user2) => {
          return user1.name < user2.name ? -1 : user1.name > user2.name ? 1 : 0
        })
    },
    csvExportData() {
      let headers = ['name', 'email', 'title', 'use code', 'magic link', 'used']
      let rows = this.tableRows.map(tRow => [
        tRow.name,
        tRow.email,
        tRow.title,
        tRow.uuid,
        this.generateMagicLink({
          useCode: tRow.uuid,
          shortcut: this.magicLinkShortcut,
          dest: this.magicLinkDestination,
          test: false
        }),
        tRow.used
      ])
      return {
        headers,
        rows
      }
    }
  },
  methods: {
    fetchReport() {
      this.loading = true
      this.$angularWorld.adminApi
        .getDomainUseCodesReport(this.domainUuid, this.reportType)
        .then(useCodes => {
          this.useCodes = useCodes || []
          this.loading = false
        })
    },
    formatTimestamp(timestamp, formatString) {
      return moment(timestamp)
        .tz('America/Los_Angeles')
        .format(formatString)
    },
    isDatePassed(isoTimestamp) {
      return isoTimestamp < new Date().toISOString()
    },
    generateMagicLink({ useCode, shortcut, dest, test }) {
      let encodedDestUrl = encodeURIComponent(dest)
      let magicLinkUrl = shortcut
        ? `${this.$angularWorld.config.accountsRoot}/uc/${useCode}/${shortcut}${
            test ? '?test=1' : ''
          }`
        : `${this.$angularWorld.config.accountsRoot}/uc/${useCode}?dest=${encodedDestUrl}${
            test ? '&test=1' : ''
          }`
      return magicLinkUrl
    },
    testUseCode(useCode) {
      let magicLinkUrl = this.generateMagicLink({
        useCode,
        shortcut: this.magicLinkShortcut,
        dest: this.magicLinkDestination,
        test: true
      })
      window.open(magicLinkUrl)
    },
    setMagicLinkDestination(product) {
      if (product === 'toolkit') {
        this.magicLinkDestination = this.$angularWorld.config.toolkitRoot
      } else if (product === 'pathways') {
        this.magicLinkDestination = this.$angularWorld.config.pathwaysRoot
      }
    }
  }
}
</script>

<style scoped lang="less">
.report-container {
  padding: 32px 0;
}
</style>
